import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  trainerApi,
  Trainer,
  TrainerSelfOnboardingPayload,
  GenderCode,
  LanguageProficiencyLevel,
  EducationLevel,
} from "../api";

const GENDER_OPTIONS: GenderCode[] = ["MALE", "FEMALE", "OTHER"];
const PROFICIENCY_OPTIONS: LanguageProficiencyLevel[] = ["BASIC", "INTERMEDIATE", "ADVANCED"];
const EDUCATION_OPTIONS: EducationLevel[] = ["GRADUATE", "POST_GRADUATE", "DOCTORATE"];

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

interface FormState {
  gender: GenderCode;
  date_of_birth: string;
  native_language: string;
  native_language_proficiency: LanguageProficiencyLevel;
  english_proficiency: LanguageProficiencyLevel;
  hindi_proficiency: LanguageProficiencyLevel;
  occupation: string;
  home_town: string;
  state: string;
  district: string;
  city: string;
  address: string;
  pin_code: string;
  whatsapp_number: string;
  education_level: EducationLevel;
  graduation_stream: string;
  post_graduation_stream: string;
  academic_qualifications: string;
  professional_qualifications: string;
  certificate_courses: string;
  formal_facilitation_training: string;
  training_institutes: string;
  employer_name: string;
  industry: string;
  years_of_experience: string;
  designation: string;
  freelance_trainer_age: string;
  top_specializations: string;
  trained_college_students: string;
  trained_entry_level_employees: string;
  trained_corporate_leaders: string;
  trained_ngo_beneficiaries: string;
  trained_educational_institutes: string;
  knowledge_updation: string;
  states_visited_count: string;
  performing_arts_experience: string;
  sports_experience: string;
  reading_preferences: string;
  social_work_details: string;
  has_office: string;
  owns_car: string;
  owns_laptop: string;
  has_high_speed_broadband: string;
  has_online_training_experience: string;
  can_travel_for_training: string;
  availability_per_month: string;
}

const emptyForm: FormState = {
  gender: "MALE",
  date_of_birth: "",
  native_language: "",
  native_language_proficiency: "BASIC",
  english_proficiency: "BASIC",
  hindi_proficiency: "BASIC",
  occupation: "",
  home_town: "",
  state: "",
  district: "",
  city: "",
  address: "",
  pin_code: "",
  whatsapp_number: "",
  education_level: "GRADUATE",
  graduation_stream: "",
  post_graduation_stream: "",
  academic_qualifications: "",
  professional_qualifications: "",
  certificate_courses: "",
  formal_facilitation_training: "NO",
  training_institutes: "",
  employer_name: "",
  industry: "",
  years_of_experience: "",
  designation: "",
  freelance_trainer_age: "",
  top_specializations: "",
  trained_college_students: "NO",
  trained_entry_level_employees: "NO",
  trained_corporate_leaders: "NO",
  trained_ngo_beneficiaries: "NO",
  trained_educational_institutes: "NO",
  knowledge_updation: "",
  states_visited_count: "",
  performing_arts_experience: "",
  sports_experience: "",
  reading_preferences: "",
  social_work_details: "",
  has_office: "NO",
  owns_car: "NO",
  owns_laptop: "NO",
  has_high_speed_broadband: "NO",
  has_online_training_experience: "NO",
  can_travel_for_training: "YES",
  availability_per_month: "",
};

const TrainerOnboarding: React.FC = () => {
  const [profile, setProfile] = useState<Trainer | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await trainerApi.getSelfProfile();
      if (response.success && response.data) {
        const trainer = response.data;
        setProfile(trainer);
        if (trainer.profile) {
          setForm({
            gender: trainer.profile.gender ?? "MALE",
            date_of_birth: trainer.profile.date_of_birth ?? "",
            native_language: trainer.profile.native_language ?? "",
            native_language_proficiency:
              trainer.profile.native_language_proficiency ?? "BASIC",
            english_proficiency: trainer.profile.english_proficiency ?? "BASIC",
            hindi_proficiency: trainer.profile.hindi_proficiency ?? "BASIC",
            occupation: trainer.profile.occupation ?? "",
            home_town: trainer.profile.home_town ?? "",
            state: trainer.profile.state ?? "",
            district: trainer.profile.district ?? "",
            city: trainer.profile.city ?? "",
            address: trainer.profile.address ?? "",
            pin_code: trainer.profile.pin_code ?? "",
            whatsapp_number: trainer.profile.whatsapp_number ?? "",
            education_level: trainer.profile.education_level ?? "GRADUATE",
            graduation_stream: trainer.profile.graduation_stream ?? "",
            post_graduation_stream: trainer.profile.post_graduation_stream ?? "",
            academic_qualifications:
              trainer.profile.academic_qualifications ?? "",
            professional_qualifications:
              trainer.profile.professional_qualifications ?? "",
            certificate_courses: trainer.profile.certificate_courses ?? "",
            formal_facilitation_training:
              trainer.profile.formal_facilitation_training ? "YES" : "NO",
            training_institutes: trainer.profile.training_institutes ?? "",
            employer_name: trainer.profile.employer_name ?? "",
            industry: trainer.profile.industry ?? "",
            years_of_experience: trainer.profile.years_of_experience?.toString() ?? "",
            designation: trainer.profile.designation ?? "",
            freelance_trainer_age:
              trainer.profile.freelance_trainer_age?.toString() ?? "",
            top_specializations: trainer.profile.top_specializations ?? "",
            trained_college_students: trainer.profile.trained_college_students ? "YES" : "NO",
            trained_entry_level_employees: trainer.profile.trained_entry_level_employees ? "YES" : "NO",
            trained_corporate_leaders: trainer.profile.trained_corporate_leaders ? "YES" : "NO",
            trained_ngo_beneficiaries: trainer.profile.trained_ngo_beneficiaries ? "YES" : "NO",
            trained_educational_institutes: trainer.profile.trained_educational_institutes ? "YES" : "NO",
            knowledge_updation: trainer.profile.knowledge_updation ?? "",
            states_visited_count: trainer.profile.states_visited_count?.toString() ?? "",
            performing_arts_experience: trainer.profile.performing_arts_experience ?? "",
            sports_experience: trainer.profile.sports_experience ?? "",
            reading_preferences: trainer.profile.reading_preferences ?? "",
            social_work_details: trainer.profile.social_work_details ?? "",
            has_office: trainer.profile.has_office ? "YES" : "NO",
            owns_car: trainer.profile.owns_car ? "YES" : "NO",
            owns_laptop: trainer.profile.owns_laptop ? "YES" : "NO",
            has_high_speed_broadband: trainer.profile.has_high_speed_broadband ? "YES" : "NO",
            has_online_training_experience:
              trainer.profile.has_online_training_experience ? "YES" : "NO",
            can_travel_for_training: trainer.profile.can_travel_for_training ? "YES" : "NO",
            availability_per_month: trainer.profile.availability_per_month ?? "",
          });
        }
        if (trainer.status === "Invited") {
          await trainerApi.startSelfOnboarding();
        }
      }
    } catch (err: any) {
      setFatalError(err.response?.data?.message ?? "Failed to load trainer profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const setField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (data: FormState) => {
    const errors: Record<string, string> = {};
    if (!data.date_of_birth) {
      errors.date_of_birth = "Date of birth is required";
    }
    const requireLength = (field: keyof FormState, label: string, min: number, max: number) => {
      const value = data[field]?.trim();
      if (!value) {
        errors[field] = `${label} is required`;
      } else if (value.length < min || value.length > max) {
        errors[field] = `${label} must be between  and  characters`;
      }
    };

    requireLength("occupation", "Occupation", 2, 500);
    requireLength("native_language", "Native language", 2, 20);
    requireLength("home_town", "Home town", 2, 20);
    requireLength("state", "State", 2, 100);
    requireLength("city", "City", 2, 20);
    requireLength("address", "Address", 2, 500);
    if (!/^\d{6}$/.test(data.pin_code)) {
      errors.pin_code = "PIN code must be 6 digits";
    }
    if (!/^\d{10}$/.test(data.whatsapp_number)) {
      errors.whatsapp_number = "WhatsApp number must be 10 digits";
    }
    requireLength("graduation_stream", "Graduation stream", 2, 20);
    if (data.post_graduation_stream && (data.post_graduation_stream.length < 2 || data.post_graduation_stream.length > 20)) {
      errors.post_graduation_stream = "Post-graduation stream must be between 2 and 20 characters";
    }
    requireLength("academic_qualifications", "Academic qualifications", 2, 50);
    requireLength("professional_qualifications", "Professional qualifications", 2, 50);
    requireLength("employer_name", "Employer name", 2, 255);
    requireLength("industry", "Industry", 2, 255);
    requireLength("designation", "Designation", 2, 255);
    requireLength("knowledge_updation", "Knowledge updation", 2, 1000);
    requireLength("availability_per_month", "Availability per month", 1, 100);

    const numericFields: Array<[keyof FormState, string]> = [
      ["years_of_experience", "Years of experience"],
      ["freelance_trainer_age", "Freelance trainer age"],
      ["states_visited_count", "States visited count"],
    ];
    numericFields.forEach(([field, label]) => {
      const value = data[field]?.trim();
      if (!value) {
        errors[field] = `${label} is required`;
        return;
      }
      if (!/^\d+$/.test(value)) {
        errors[field] = `${label} must be numeric`;
      }
    });

    if (data.formal_facilitation_training === "YES" && !data.training_institutes.trim()) {
      errors.training_institutes = "Please mention training institute";
    }

    return errors;
  };

  const buildPayload = (data: FormState): TrainerSelfOnboardingPayload => ({
    gender: data.gender,
    date_of_birth: data.date_of_birth,
    native_language: data.native_language.trim(),
    native_language_proficiency: data.native_language_proficiency,
    english_proficiency: data.english_proficiency,
    hindi_proficiency: data.hindi_proficiency,
    occupation: data.occupation.trim(),
    home_town: data.home_town.trim(),
    state: data.state.trim(),
    district: data.district.trim(),
    city: data.city.trim(),
    address: data.address.trim(),
    pin_code: data.pin_code.trim(),
    whatsapp_number: data.whatsapp_number.trim(),
    education_level: data.education_level,
    graduation_stream: data.graduation_stream.trim(),
    post_graduation_stream: data.post_graduation_stream.trim(),
    academic_qualifications: data.academic_qualifications.trim(),
    professional_qualifications: data.professional_qualifications.trim(),
    certificate_courses: data.certificate_courses.trim(),
    formal_facilitation_training: data.formal_facilitation_training === "YES",
    training_institutes: data.training_institutes.trim(),
    employer_name: data.employer_name.trim(),
    industry: data.industry.trim(),
    years_of_experience: Number(data.years_of_experience),
    designation: data.designation.trim(),
    freelance_trainer_age: Number(data.freelance_trainer_age),
    top_specializations: data.top_specializations.trim(),
    trained_college_students: data.trained_college_students === "YES",
    trained_entry_level_employees: data.trained_entry_level_employees === "YES",
    trained_corporate_leaders: data.trained_corporate_leaders === "YES",
    trained_ngo_beneficiaries: data.trained_ngo_beneficiaries === "YES",
    trained_educational_institutes: data.trained_educational_institutes === "YES",
    knowledge_updation: data.knowledge_updation.trim(),
    states_visited_count: Number(data.states_visited_count),
    performing_arts_experience: data.performing_arts_experience.trim(),
    sports_experience: data.sports_experience.trim(),
    reading_preferences: data.reading_preferences.trim(),
    social_work_details: data.social_work_details.trim(),
    has_office: data.has_office === "YES",
    owns_car: data.owns_car === "YES",
    owns_laptop: data.owns_laptop === "YES",
    has_high_speed_broadband: data.has_high_speed_broadband === "YES",
    has_online_training_experience: data.has_online_training_experience === "YES",
    can_travel_for_training: data.can_travel_for_training === "YES",
    availability_per_month: data.availability_per_month.trim(),
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    try {
      setSubmitting(true);
      const payload = buildPayload(form);
      await trainerApi.completeSelfOnboarding(payload);
      setSuccess("Onboarding submitted successfully.");
      loadProfile();
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, __global: err.response?.data?.message ?? "Submission failed." }));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (fatalError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {fatalError}
      </div>
    );
  }

  if (profile?.status === "Completed") {
    return (
      <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded">
        Your onboarding is already complete. Thank you!
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-sm border rounded-lg p-6 space-y-2">
        <h1 className="text-xl font-semibold text-gray-900">Trainer Onboarding</h1>
        <p className="text-sm text-gray-600">
          Please fill in the details below. Fields marked with * are mandatory as per the accreditation guidelines.
        </p>
        {errors.__global && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {errors.__global}
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded">
            {success}
          </div>
        )}
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <Section title="Basic details">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SelectField
              label="Gender *"
              value={form.gender}
              onChange={(value) => setField("gender", value)}
              options={GENDER_OPTIONS.map((value) => ({ value, label: value }))}
            />
            <InputField
              label="Date of birth *"
              type="date"
              value={form.date_of_birth}
              onChange={(event) => setField("date_of_birth", event.target.value)}
              error={errors.date_of_birth}
            />
            <SelectField
              label="State *"
              value={form.state}
              onChange={(value) => setField("state", value)}
              options={INDIAN_STATES.map((label) => ({ value: label, label }))}
              allowEmpty
              error={errors.state}
            />
            <InputField
              label="District"
              value={form.district}
              onChange={(event) => setField("district", event.target.value)}
              error={errors.district}
            />
            <InputField
              label="City *"
              value={form.city}
              onChange={(event) => setField("city", event.target.value)}
              error={errors.city}
            />
            <InputField
              label="Home town *"
              value={form.home_town}
              onChange={(event) => setField("home_town", event.target.value)}
              error={errors.home_town}
            />
            <InputField
              label="Native language *"
              value={form.native_language}
              onChange={(event) => setField("native_language", event.target.value)}
              error={errors.native_language}
            />
            <SelectField
              label="Native language proficiency *"
              value={form.native_language_proficiency}
              onChange={(value) => setField("native_language_proficiency", value)}
              options={PROFICIENCY_OPTIONS.map((value) => ({ value, label: value }))}
            />
            <SelectField
              label="English proficiency *"
              value={form.english_proficiency}
              onChange={(value) => setField("english_proficiency", value)}
              options={PROFICIENCY_OPTIONS.map((value) => ({ value, label: value }))}
            />
            <SelectField
              label="Hindi proficiency *"
              value={form.hindi_proficiency}
              onChange={(value) => setField("hindi_proficiency", value)}
              options={PROFICIENCY_OPTIONS.map((value) => ({ value, label: value }))}
            />
            <InputField
              label="Address *"
              value={form.address}
              onChange={(event) => setField("address", event.target.value)}
              error={errors.address}
            />
            <InputField
              label="PIN code *"
              value={form.pin_code}
              onChange={(event) => setField("pin_code", event.target.value)}
              error={errors.pin_code}
            />
            <InputField
              label="WhatsApp number *"
              value={form.whatsapp_number}
              onChange={(event) => setField("whatsapp_number", event.target.value)}
              error={errors.whatsapp_number}
            />
          </div>
        </Section>

        <Section title="Professional details">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField
              label="Occupation *"
              value={form.occupation}
              onChange={(event) => setField("occupation", event.target.value)}
              error={errors.occupation}
            />
            <SelectField
              label="Education level *"
              value={form.education_level}
              onChange={(value) => setField("education_level", value)}
              options={EDUCATION_OPTIONS.map((value) => ({ value, label: value.replace("_", " ") }))}
            />
            <InputField
              label="Graduation stream *"
              value={form.graduation_stream}
              onChange={(event) => setField("graduation_stream", event.target.value)}
              error={errors.graduation_stream}
            />
            <InputField
              label="Post graduation stream"
              value={form.post_graduation_stream}
              onChange={(event) => setField("post_graduation_stream", event.target.value)}
              error={errors.post_graduation_stream}
            />
            <InputField
              label="Academic qualifications *"
              value={form.academic_qualifications}
              onChange={(event) => setField("academic_qualifications", event.target.value)}
              error={errors.academic_qualifications}
            />
            <InputField
              label="Professional qualifications *"
              value={form.professional_qualifications}
              onChange={(event) => setField("professional_qualifications", event.target.value)}
              error={errors.professional_qualifications}
            />
            <InputField
              label="Certificate courses"
              value={form.certificate_courses}
              onChange={(event) => setField("certificate_courses", event.target.value)}
              error={errors.certificate_courses}
            />
            <SelectField
              label="Formal facilitation training *"
              value={form.formal_facilitation_training}
              onChange={(value) => setField("formal_facilitation_training", value)}
              options={["YES", "NO"].map((value) => ({ value, label: value }))}
            />
            <InputField
              label="Training institutes"
              value={form.training_institutes}
              onChange={(event) => setField("training_institutes", event.target.value)}
              error={errors.training_institutes}
            />
            <InputField
              label="Employer name *"
              value={form.employer_name}
              onChange={(event) => setField("employer_name", event.target.value)}
              error={errors.employer_name}
            />
            <InputField
              label="Industry *"
              value={form.industry}
              onChange={(event) => setField("industry", event.target.value)}
              error={errors.industry}
            />
            <InputField
              label="Designation *"
              value={form.designation}
              onChange={(event) => setField("designation", event.target.value)}
              error={errors.designation}
            />
            <InputField
              label="Years of experience *"
              value={form.years_of_experience}
              onChange={(event) => setField("years_of_experience", event.target.value)}
              error={errors.years_of_experience}
            />
            <InputField
              label="Freelance trainer age *"
              value={form.freelance_trainer_age}
              onChange={(event) => setField("freelance_trainer_age", event.target.value)}
              error={errors.freelance_trainer_age}
            />
            <TextAreaField
              label="Top specialisations *"
              value={form.top_specializations}
              onChange={(event) => setField("top_specializations", event.target.value)}
              error={errors.top_specializations}
            />
          </div>
        </Section>

        <Section title="Training experience">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <YesNoField
              label="Trained college students *"
              value={form.trained_college_students}
              onChange={(value) => setField("trained_college_students", value)}
            />
            <YesNoField
              label="Trained entry-level employees *"
              value={form.trained_entry_level_employees}
              onChange={(value) => setField("trained_entry_level_employees", value)}
            />
            <YesNoField
              label="Trained corporate leaders *"
              value={form.trained_corporate_leaders}
              onChange={(value) => setField("trained_corporate_leaders", value)}
            />
            <YesNoField
              label="Trained NGO beneficiaries *"
              value={form.trained_ngo_beneficiaries}
              onChange={(value) => setField("trained_ngo_beneficiaries", value)}
            />
            <YesNoField
              label="Trained educational institutes *"
              value={form.trained_educational_institutes}
              onChange={(value) => setField("trained_educational_institutes", value)}
            />
            <InputField
              label="States visited *"
              value={form.states_visited_count}
              onChange={(event) => setField("states_visited_count", event.target.value)}
              error={errors.states_visited_count}
            />
            <TextAreaField
              label="Knowledge updation *"
              value={form.knowledge_updation}
              onChange={(event) => setField("knowledge_updation", event.target.value)}
              error={errors.knowledge_updation}
            />
            <TextAreaField
              label="Performing arts"
              value={form.performing_arts_experience}
              onChange={(event) => setField("performing_arts_experience", event.target.value)}
            />
            <TextAreaField
              label="Sports"
              value={form.sports_experience}
              onChange={(event) => setField("sports_experience", event.target.value)}
            />
            <TextAreaField
              label="Reading preferences"
              value={form.reading_preferences}
              onChange={(event) => setField("reading_preferences", event.target.value)}
            />
            <TextAreaField
              label="Social work"
              value={form.social_work_details}
              onChange={(event) => setField("social_work_details", event.target.value)}
            />
          </div>
        </Section>

        <Section title="Logistics & availability">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <YesNoField
              label="Own office *"
              value={form.has_office}
              onChange={(value) => setField("has_office", value)}
            />
            <YesNoField
              label="Own car *"
              value={form.owns_car}
              onChange={(value) => setField("owns_car", value)}
            />
            <YesNoField
              label="Own laptop *"
              value={form.owns_laptop}
              onChange={(value) => setField("owns_laptop", value)}
            />
            <YesNoField
              label="High-speed broadband *"
              value={form.has_high_speed_broadband}
              onChange={(value) => setField("has_high_speed_broadband", value)}
            />
            <YesNoField
              label="Online training experience *"
              value={form.has_online_training_experience}
              onChange={(value) => setField("has_online_training_experience", value)}
            />
            <YesNoField
              label="Can travel *"
              value={form.can_travel_for_training}
              onChange={(value) => setField("can_travel_for_training", value)}
            />
            <InputField
              label="Availability per month (days) *"
              value={form.availability_per_month}
              onChange={(event) => setField("availability_per_month", event.target.value)}
              error={errors.availability_per_month}
            />
          </div>
        </Section>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit onboarding"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="bg-white shadow-sm border rounded-lg p-6 space-y-4">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {children}
  </section>
);

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const InputField: React.FC<InputFieldProps> = ({ label, error, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input {...props} className="input mt-1" />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
}> = ({ label, value, onChange, error }) => (
  <div className="md:col-span-3">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <textarea value={value} onChange={onChange} className="input mt-1 h-24" />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  allowEmpty?: boolean;
  error?: string;
}> = ({ label, value, onChange, options, allowEmpty, error }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="input mt-1"
    >
      {allowEmpty && <option value="">Select</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const YesNoField: React.FC<{
  label: string;
  value: string;
  onChange: (value: "YES" | "NO") => void;
}> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="mt-1 flex gap-4">
      {(["YES", "NO"] as const).map((option) => (
        <label key={option} className="flex items-center text-sm text-gray-700 gap-2">
          <input
            type="radio"
            className="text-indigo-600"
            checked={value === option}
            onChange={() => onChange(option)}
          />
          {option}
        </label>
      ))}
    </div>
  </div>
);

export default TrainerOnboarding;
